import React, { useState } from 'react';
import { Card, CardBody, Col, FormGroup, Input, Label, Row, Button, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getCustomersFindOne, updateCustomer } from '../../../slices/customers/thunk';

interface CarrierSectionProps {
    values: {
        carrierIdType: string;
        carrierId: string;
        carrierName: string;
        carrierEmail: string;
    };
    touched: {
        carrierIdType?: boolean;
        carrierId?: boolean;
        carrierName?: boolean;
        carrierEmail?: boolean;
    };
    errors: {
        carrierIdType?: string;
        carrierId?: string;
        carrierName?: string;
        carrierEmail?: string;
    };
    onChange: (field: string, value: any) => void;
    formik: any;
    documentTypes?: any[];
}

const CarrierSection: React.FC<CarrierSectionProps> = ({
    values,
    touched,
    errors,
    onChange,
    formik,
    documentTypes = []
}) => {
    const dispatch = useDispatch<any>();
    const [isSearching, setIsSearching] = useState(false);
    const [suggestions, setSuggestions] = useState<any[]>([]);

    const handleIdChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.carrierIdType);
        if (selectedDocType) {
            const docName = selectedDocType.name.toUpperCase();
            if (docName.includes('CÉDULA') || docName.includes('RUC')) {
                if (!/^\d+$/.test(value)) return;
            }
        }

        if (!formik.values.carrierIdType) {
            formik.setFieldTouched('carrierIdType', true, false);
            formik.setFieldError('carrierIdType', 'Tipo de identificación es requerido');
        }

        onChange('carrierId', value);
        formik.handleChange(e);

        if (value === '') {
            clearCarrierFields();
        }
        
        validateIdentification(value);
    };

    const searchOnServer = async (identification: string) => {
        if (!identification || formik.errors.carrierId) {
            return;
        }

        setIsSearching(true);

        try {
            const result = await dispatch(getCustomersFindOne({
                identificationNumber: identification
                // Sin filtros de isCustomer/isCarrier para encontrar a cualquiera
            })).unwrap();

            let foundCarrier = null;
            if (Array.isArray(result)) {
                foundCarrier = result.find((c: any) => c.identificationNumber === identification);
            } else if (result && typeof result === 'object') {
                if (result.identificationNumber === identification) {
                    foundCarrier = result;
                }
            }

            if (foundCarrier) {
                // Si existe y no es carrier, actualizarlo a carrier
                if (!foundCarrier.isCarrier) {
                    try {
                        await dispatch(updateCustomer({
                            customer: {
                                ...foundCarrier,
                                isCarrier: true
                            }
                        })).unwrap();
                        
                        toast.success("Transportista encontrado y actualizado correctamente");
                    } catch (updateError) {
                        console.error("Error actualizando transportista:", updateError);
                        toast.error("Error al actualizar transportista a carrier");
                    }
                } else {
                    toast.success("Transportista encontrado");
                }
                
                fillCarrierData(foundCarrier);
            } else {
                toast.info("Transportista no encontrado. Puede registrarlo.");
                clearCarrierFields();
            }
        } catch (error) {
            console.error("Error buscando transportista:", error);
            toast.error("Error al buscar el transportista. Intente de nuevo.");
            clearCarrierFields();
        } finally {
            setIsSearching(false);
        }
    };

    const fillCarrierData = (carrier: any) => {
        onChange('carrierName', carrier.name || '');
        onChange('carrierEmail', carrier.email || '');
        
        // Usar directamente el tipo de identificación de la respuesta de la API
        if (carrier.identityDocumentType && carrier.identityDocumentType.id) {
            // Solo actualizar si el campo está vacío o si es diferente
            if (!formik.values.carrierIdType || formik.values.carrierIdType !== carrier.identityDocumentType.id) {
                formik.setFieldValue('carrierIdType', carrier.identityDocumentType.id);
            }
        }
    };

    const clearCarrierFields = () => {
        onChange('carrierName', '');
        onChange('carrierEmail', '');
        formik.setFieldValue('carrierIdType', '');
        formik.setFieldTouched('carrierName', false);
        formik.setFieldError('carrierName', '');
        formik.setFieldTouched('carrierEmail', false);
        formik.setFieldError('carrierEmail', '');
        formik.setFieldTouched('carrierIdType', false);
        formik.setFieldError('carrierIdType', '');
    };

    const validateIdentification = (value: string) => {
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.carrierIdType);
        if (selectedDocType) {
            const docName = selectedDocType.name.toUpperCase();
            
            if (docName.includes('RUC')) {
                if (!/^\d{13}$/.test(value)) {
                    formik.setFieldError('carrierId', 'El RUC debe tener 13 dígitos');
                } else {
                    formik.setFieldError('carrierId', '');
                }
            } else if (docName.includes('CÉDULA') || docName.includes('CEDULA')) {
                if (!/^\d{10}$/.test(value)) {
                    formik.setFieldError('carrierId', 'La cédula debe tener 10 dígitos');
                } else {
                    formik.setFieldError('carrierId', '');
                }
            }
        }
    };

    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(field, e.target.value);
    };

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-truck-line me-2 fs-18"></i>
                    Información del Transportista
                </h5>
            </div>
            <CardBody className="p-4 bg-white">
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Tipo identificación <span className="text-danger">*</span>
                            </Label>
                            <Input 
                                type="select"
                                name="carrierIdType"
                                className="form-select"
                                value={values.carrierIdType}
                                onChange={handleInputChange('carrierIdType')}
                                invalid={!!(touched.carrierIdType && errors.carrierIdType)}
                            >
                                <option value="">Seleccionar...</option>
                                {documentTypes.map((type: any) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name || type.description || type.code}
                                    </option>
                                ))}
                            </Input>
                            {touched.carrierIdType && errors.carrierIdType && (
                                <div className="invalid-feedback">{errors.carrierIdType}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0 position-relative">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Identificación <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-profile-line text-muted"></i>
                                </span>
                                <Input 
                                    name="carrierId"
                                    className="form-control border-start-0 ps-1"
                                    value={values.carrierId}
                                    onChange={handleIdChangeLocal}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                                            e.preventDefault();
                                        }
                                        if (e.key === 'Enter' && values.carrierId) {
                                            e.preventDefault();
                                            searchOnServer(values.carrierId);
                                        }
                                    }}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pastedText = e.clipboardData.getData('text');
                                        const numbersOnly = pastedText.replace(/[^0-9]/g, '');
                                        onChange('carrierId', numbersOnly);
                                    }}
                                    invalid={!!(touched.carrierId && errors.carrierId)}
                                    placeholder="Ingrese identificación"
                                    inputMode="numeric"
                                />
                                <Button 
                                    color="primary"
                                    className="btn-icon"
                                    onClick={() => values.carrierId && searchOnServer(values.carrierId)}
                                    disabled={isSearching || !values.carrierId}
                                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                >
                                    {isSearching ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <i className="ri-search-line"></i>
                                    )}
                                </Button>
                                {touched.carrierId && errors.carrierId && (
                                    <div className="invalid-feedback">{errors.carrierId}</div>
                                )}
                            </div>
                            
                            {/* Sugerencias de autocompletado */}
                            {suggestions.length > 0 && (
                                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" 
                                     style={{ zIndex: 1000, top: '100%' }}>
                                    {suggestions.map((suggestion, index) => (
                                        <div 
                                            key={index}
                                            className="px-3 py-2 border-bottom cursor-pointer hover-bg-light"
                                            onClick={() => {
                                                fillCarrierData({
                                                    name: suggestion.name.split(' - ')[1],
                                                    email: 'transportista@ejemplo.com'
                                                });
                                                setSuggestions([]);
                                            }}
                                        >
                                            <small className="text-muted">{suggestion.name}</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Razón social <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-user-star-line text-muted"></i>
                                </span>
                                <Input 
                                    name="carrierName"
                                    className="form-control border-start-0 ps-1"
                                    value={values.carrierName}
                                    onChange={handleInputChange('carrierName')}
                                    invalid={!!(touched.carrierName && errors.carrierName)}
                                    placeholder="Nombre o Razón Social"
                                />
                                {touched.carrierName && errors.carrierName && (
                                    <div className="invalid-feedback">{errors.carrierName}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Correo electrónico <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-mail-line text-muted"></i>
                                </span>
                                <Input 
                                    type="email"
                                    name="carrierEmail"
                                    className="form-control border-start-0 ps-1"
                                    value={values.carrierEmail}
                                    onChange={handleInputChange('carrierEmail')}
                                    invalid={!!(touched.carrierEmail && errors.carrierEmail)}
                                    placeholder="transportista@email.com"
                                />
                                {touched.carrierEmail && errors.carrierEmail && (
                                    <div className="invalid-feedback">{errors.carrierEmail}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default CarrierSection;
